import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	InputLabel,
	Skeleton,
	Stack,
	styled,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { useState, useMemo, useEffect } from 'react';
import { TbTrash } from 'react-icons/tb';
import { useApi } from '../../../hooks/useApi';
import {
	useGlobalDispatch,
	useGlobalState,
} from '../../../stateProviders/GlobalStateProvider';
import { CompanyUpdateParams } from '../../../types/api/Company';
import { useUnsavedFormChanges } from '../../../hooks/useUnsavedFormChanges';
import { useUnsavedChangesContext } from '../../../contexts/UnsavedChangesContext';

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

export const CompanyProfile = () => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const [isSaving, setIsSaving] = useState(false);
	const globalState = useGlobalState();
	const globalDispatch = useGlobalDispatch();
	const [profilePicture, setProfilePicture] = useState(
		globalState.company?.profilePicture || '/avatar.jpg'
	);
	const api = useApi();
	const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
		null
	);
	const [name, setName] = useState(globalState.company?.name || '');
	const [email, setEmail] = useState(globalState.company?.email || '');
	const [website, setWebsite] = useState(globalState.company?.website || '');
	const [address, setAddress] = useState(
		(globalState.company as any)?.address || ''
	);
	const [removingProfilePicture, setRemovingProfilePicture] = useState(false);
	const [isLoading, setIsLoading] = useState(!globalState.company);

	const savedFormData = useMemo(
		() => ({
			name: globalState.company?.name || '',
			email: globalState.company?.email || '',
			website: globalState.company?.website || '',
			address: (globalState.company as any)?.address || '',
			profilePicture: globalState.company?.profilePicture || '/avatar.jpg',
		}),
		[globalState.company]
	);

	const currentFormData = useMemo(
		() => ({
			name,
			email,
			website,
			address,
			profilePicture: profilePictureFile ? 'changed' : profilePicture,
		}),
		[name, email, website, address, profilePicture, profilePictureFile]
	);

	const { isDirty, markAsClean, resetToSaved } = useUnsavedFormChanges(
		currentFormData,
		savedFormData
	);

	const { setHasUnsavedChanges, setOnSaveCallback } =
		useUnsavedChangesContext();

	useEffect(() => {
		if (globalState.company) {
			setName(globalState.company.name || '');
			setEmail(globalState.company.email || '');
			setWebsite(globalState.company.website || '');
			setAddress((globalState.company as any).address || '');
			setProfilePicture(globalState.company.profilePicture || '/avatar.jpg');
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [globalState.company]);

	const removeProfilePicture = async () => {
		if (!globalState.company?._id) return;
		try {
			setRemovingProfilePicture(true);
			if (!globalState.company.profilePicture) return;
			await api.company.deleteProfilePicture(globalState.company._id);
			setProfilePictureFile(null);
			setProfilePicture('/avatar.jpg');
		} catch (error) {
		} finally {
			setRemovingProfilePicture(false);
		}
	};

	const updateProfile = async (event?: React.FormEvent<HTMLFormElement>) => {
		if (event) event.preventDefault();
		if (!globalState.company?._id) return;

		const formData = new FormData();
		formData.append('name', name);
		formData.append('email', email);
		formData.append('website', website);
		formData.append('address', address);
		if (profilePictureFile) {
			formData.append('profilePicture', profilePictureFile);
		}

		try {
			setIsSaving(true);
			await api.company.update(
				globalState.company._id,
				Object.fromEntries(formData) as unknown as CompanyUpdateParams
			);
			const updatedCompany = await api.company.getCurrent();
			globalDispatch({ company: updatedCompany });
			setProfilePicture(updatedCompany.profilePicture);
			markAsClean();
		} catch (error) {
		} finally {
			setIsSaving(false);
			setProfilePictureFile(null);
		}
	};

	useEffect(() => {
		setHasUnsavedChanges(isDirty);
	}, [isDirty, setHasUnsavedChanges]);

	useEffect(() => {
		if (isDirty) {
			setOnSaveCallback(() => async () => {
				await updateProfile();
			});
		} else {
			setOnSaveCallback(null);
		}
	}, [isDirty, name, email, website, address, profilePictureFile]);

	useEffect(() => {
		return () => {
			setHasUnsavedChanges(false);
			setOnSaveCallback(null);
		};
	}, [setHasUnsavedChanges, setOnSaveCallback]);

	return (
		<Box
			component="form"
			onSubmit={updateProfile}
			sx={{
				maxWidth: { xs: '100%', sm: 600, md: 800, lg: 1000 },
				rowGap: { xs: theme.spacing(3), sm: theme.spacing(4) },
				...theme.typography.body1,
				backgroundColor: theme.palette.background.paper,
			}}
		>
			<Stack
				direction={isSmallScreen ? 'column' : 'row'}
				spacing={4}
				alignItems={isSmallScreen ? 'flex-start' : 'center'}
				sx={{ mb: theme.spacing(4) }}
			>
				<Avatar
					src={
						profilePictureFile
							? URL.createObjectURL(profilePictureFile)
							: profilePicture
					}
					alt="Profile Photo"
					sx={{
						width: 112,
						height: 112,
						bgcolor: theme.palette.gray[200],
					}}
					slotProps={{
						img: {
							referrerPolicy: 'no-referrer',
							style: { objectFit: 'contain' },
						},
					}}
				/>
				<Stack direction="column" spacing={1} alignItems="flex-start">
					<Button
						component="label"
						variant="outlined"
						color="primary"
						tabIndex={-1}
						disabled={isLoading || isSaving}
						sx={{ fontWeight: 500 }}
					>
						Upload Avatar
						<VisuallyHiddenInput
							type="file"
							accept=".jpg,.jpeg,.png,.webp"
							onChange={(event) =>
								setProfilePictureFile(event.target.files?.[0] || null)
							}
						/>
					</Button>
					<Button
						color="error"
						variant="text"
						size="small"
						startIcon={<TbTrash />}
						disabled={
							isLoading ||
							isSaving ||
							removingProfilePicture ||
							!globalState.company?.profilePicture ||
							profilePicture === '/avatar.jpg'
						}
						onClick={removeProfilePicture}
						sx={{ fontWeight: 500 }}
					>
						{removingProfilePicture ? 'Removing...' : 'Remove'}
					</Button>
					{profilePictureFile && (
						<Typography
							variant="xs"
							component="span"
							sx={{
								fontStyle: 'italic',
								color: theme.palette.gray[600],
								width: '100%',
							}}
						>
							{profilePictureFile.name}
						</Typography>
					)}
				</Stack>
			</Stack>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
					gap: theme.spacing(3),
				}}
			>
				<Box>
					<InputLabel
						sx={{
							mb: theme.spacing(1),
							color: theme.palette.secondary[800],
							fontWeight: 500,
						}}
					>
						Company Name
					</InputLabel>
					<TextField
						fullWidth
						name="name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						disabled={isLoading || isSaving}
						InputProps={{
							...(isLoading && {
								startAdornment: <Skeleton variant="text" width="100%" />,
							}),
						}}
						sx={{
							...(isLoading && {
								'& .MuiInputBase-input': {
									visibility: 'hidden',
								},
							}),
						}}
					/>
				</Box>
				<Box>
					<InputLabel
						sx={{
							mb: theme.spacing(1),
							color: theme.palette.secondary[800],
							fontWeight: 500,
						}}
					>
						Company Email
					</InputLabel>
					<TextField
						fullWidth
						name="email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						disabled={isLoading || isSaving}
						InputProps={{
							...(isLoading && {
								startAdornment: <Skeleton variant="text" width="100%" />,
							}),
						}}
						sx={{
							...(isLoading && {
								'& .MuiInputBase-input': {
									visibility: 'hidden',
								},
							}),
						}}
					/>
				</Box>
				<Box>
					<InputLabel
						sx={{
							mb: theme.spacing(1),
							color: theme.palette.secondary[800],
							fontWeight: 500,
						}}
					>
						Company Website
					</InputLabel>
					<TextField
						fullWidth
						name="website"
						type="url"
						value={website}
						onChange={(e) => setWebsite(e.target.value)}
						disabled={isLoading || isSaving}
						InputProps={{
							...(isLoading && {
								startAdornment: <Skeleton variant="text" width="100%" />,
							}),
						}}
						sx={{
							...(isLoading && {
								'& .MuiInputBase-input': {
									visibility: 'hidden',
								},
							}),
						}}
					/>
				</Box>
				<Box>
					<InputLabel
						sx={{
							mb: theme.spacing(1),
							color: theme.palette.secondary[800],
							fontWeight: 500,
						}}
					>
						Company Address
					</InputLabel>
					<TextField
						fullWidth
						name="address"
						value={address}
						onChange={(e) => setAddress(e.target.value)}
						disabled={isLoading || isSaving}
						InputProps={{
							...(isLoading && {
								startAdornment: <Skeleton variant="text" width="100%" />,
							}),
						}}
						sx={{
							...(isLoading && {
								'& .MuiInputBase-input': {
									visibility: 'hidden',
								},
							}),
						}}
					/>
				</Box>
			</Box>

			<Box
				sx={{
					height: 40,
					fontWeight: 600,
					mt: theme.spacing(4),
					display: 'flex',
					alignItems: 'center',
				}}
			>
				<Button
					type="submit"
					variant="contained"
					color="success"
					disabled={isLoading || isSaving}
					sx={{ fontWeight: 600, fontSize: theme.typography.sm }}
				>
					{isSaving ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: theme.spacing(1),
							}}
						>
							<CircularProgress size={18} color="success" />
							<Typography variant="xs" component="span" fontWeight={600}>
								Saving...
							</Typography>
						</Box>
					) : (
						'Save Company Profile'
					)}
				</Button>
			</Box>
		</Box>
	);
};
